import compact from 'lodash-es/compact'
import map from 'lodash-es/map'

import { EMPTY_LIST } from '../../../constants/common'
import {
  DOC_FLOW_RESULT_SAVE_TO_PROFILE_STATUS,
  DOC_FLOW_RESULT_SAVE_TO_PROFILE_STATUSES,
  DOC_FLOW_RESULT_SEND_EMAIL_STATUS,
} from '../../../constants/docFlow'
import { PROFILE_ROUTE_NAMES } from '../../../constants/route'
import { getEmailProfilePathname } from '../../../utils/link'
import { formatNumberWithSeparators } from '../../../utils/number'
import {
  TableDateCell,
  TableEmptyCell,
  TableFileListCell,
  TableRoleCell,
  TableStatusCell,
  TableTextCell,
  TableUserCell,
  TextListCell,
  toTableCellMultiValue,
} from '../../Table'
import { toRecipientArchivedMultiValue } from '../DocFlow.utils.recipients'
import {
  DocFlowResultTableCellName,
  getDocFlowResultEmailStatuses,
} from '../DocFlowResult'
import {
  DocFlowDateLinkCell,
  DocFlowTableCellBuilding,
  DocFlowTableCellFlat,
  DocFlowTableCellRecipientStatus,
} from '../DocFlowTableCell'

import { EpostStatusCell } from './EpostStatusCell'
import { RegisteredLetterCell } from './RegisteredLetterCell'

// The primary message stays first, the additional ones collapse into the counter.
function toEmailAddressesCellValue(email) {
  const addresses = map(
    [email, ...(email?.additional_messages ?? EMPTY_LIST)],
    'address'
  )

  return map(compact(addresses), toTableCellMultiValue)
}

export function renderCell({
  availableSections,
  canResendEpost,
  disabledResendPost,
  item,
  name,
  preparedFiles,
  scrollRef,
  t,
  onClickFile,
  onEpostResend,
  ...restProps
}) {
  const { [name]: value } = item
  const recipientValue = item[DocFlowResultTableCellName.Recipient]?.[name]
  const email = item.email

  switch (name) {
    case DocFlowResultTableCellName.Building:
      return (
        <DocFlowTableCellBuilding
          {...restProps}
          scrollRef={scrollRef}
          value={value}
        />
      )

    case DocFlowResultTableCellName.File:
      return (
        <TableFileListCell
          {...restProps}
          listProps={{
            moreItemsProps: {
              onClickItem: onClickFile,
            },
            onClickItem: onClickFile,
          }}
          scrollRef={scrollRef}
          value={preparedFiles}
        />
      )

    case DocFlowResultTableCellName.Flat:
      return (
        <DocFlowTableCellFlat
          {...restProps}
          item={item}
          scrollRef={scrollRef}
          value={value}
        />
      )

    case DocFlowResultTableCellName.Role:
      return <TableRoleCell {...restProps} value={recipientValue} />

    case DocFlowResultTableCellName.Status:
      return (
        <DocFlowTableCellRecipientStatus
          {...restProps}
          value={recipientValue}
        />
      )

    case DocFlowResultTableCellName.Recipient:
      return (
        <TableUserCell
          {...restProps}
          value={toRecipientArchivedMultiValue(value)}
        />
      )

    case DocFlowResultTableCellName.SavedToProfileAt:
      return (
        <DocFlowDateLinkCell
          {...restProps}
          link={PROFILE_ROUTE_NAMES.inhabitant({
            additionalParams: `/documents/${item[DocFlowResultTableCellName.SavedToProfileDirectoryId]}`,
            id: item[DocFlowResultTableCellName.Recipient]?.id,
          })}
          linkTooltip={t('DocumentStapling.GoToProfileTooltipKey')}
          useTodayFormat={false}
          value={value}
        />
      )

    case DocFlowResultTableCellName.SavedToProfileStatus:
      return value ? (
        <TableStatusCell
          {...restProps}
          statuses={DOC_FLOW_RESULT_SAVE_TO_PROFILE_STATUSES}
          value={DOC_FLOW_RESULT_SAVE_TO_PROFILE_STATUS[value]}
        />
      ) : (
        <TableEmptyCell {...restProps} />
      )

    case DocFlowResultTableCellName.EmailAddress:
      return (
        <TextListCell
          {...restProps}
          scrollRef={scrollRef}
          value={toEmailAddressesCellValue(email)}
        />
      )

    case DocFlowResultTableCellName.EmailCreatedAt:
      return (
        <DocFlowDateLinkCell
          {...restProps}
          link={getEmailProfilePathname({
            availableSections,
            id: email?.uuid,
            singleEmailAccount: true,
          })}
          linkTooltip={t('DocumentStapling.GoToEmailProfileTooltipKey')}
          useTodayFormat={false}
          value={email?.created_at}
        />
      )

    case DocFlowResultTableCellName.EmailStatus:
      return email?.status ? (
        <TableStatusCell
          {...restProps}
          statuses={getDocFlowResultEmailStatuses({ errors: email.errors, t })}
          value={DOC_FLOW_RESULT_SEND_EMAIL_STATUS[email.status]}
        />
      ) : (
        <TableEmptyCell {...restProps} />
      )

    case DocFlowResultTableCellName.EpostSentAt:
      return <TableDateCell {...restProps} value={value} />

    case DocFlowResultTableCellName.EpostSpentCredits:
      return (
        <TableTextCell
          {...restProps}
          value={value ? formatNumberWithSeparators(value) : value}
        />
      )

    case DocFlowResultTableCellName.EpostStatus:
      return value ? (
        <EpostStatusCell
          {...restProps}
          canResendEpost={canResendEpost}
          disabledResendPost={disabledResendPost}
          item={item}
          value={value}
          onEpostResend={onEpostResend}
        />
      ) : (
        <TableEmptyCell {...restProps} />
      )

    case DocFlowResultTableCellName.RegisteredLetter:
      return <RegisteredLetterCell {...restProps} value={value} />

    default:
      break
  }
}
